import { asRecord, asString, reportDtoAnomaly } from './common';

/** One changelog fragment (one PR) from GET /api/release-notes. */
export interface ReleaseNoteEntry {
  /** Calendar day `RRRR-MM-DD` from the fragment file name. */
  date: string;
  slug: string;
  /** Markdown-light bullet texts: plain text plus `**bold**`, never HTML. */
  items: string[];
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

export function normalizeReleaseNoteEntry(raw: unknown): ReleaseNoteEntry | null {
  const record = asRecord(raw);
  const date = asString(record?.date);
  const slug = asString(record?.slug);
  if (!record || !ISO_DAY.test(date) || !slug || !Array.isArray(record.items)) {
    reportDtoAnomaly('releaseNotes', 'entry', raw);
    return null;
  }

  const items = record.items
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter(Boolean);
  if (items.length === 0) {
    reportDtoAnomaly('releaseNotes', 'items', raw);
    return null;
  }

  return { date, slug, items };
}

/**
 * The whole list. A payload that is not an array is a contract error (the
 * modal shows the load error), not an empty changelog.
 */
export function normalizeReleaseNotes(raw: unknown): ReleaseNoteEntry[] {
  if (!Array.isArray(raw)) {
    reportDtoAnomaly('releaseNotes', 'list', raw);
    throw new Error('Server returned an invalid release notes payload');
  }

  return raw
    .map((entry) => normalizeReleaseNoteEntry(entry))
    .filter((entry): entry is ReleaseNoteEntry => entry !== null);
}
